'use strict';

import * as _ from 'lodash-es';

(function () {
  angular.module('specsguardApp')
    .service('ModuleSubmoduleElements', ModuleSubmoduleElements);

  ModuleSubmoduleElements.$inject = [];

  function ModuleSubmoduleElements () {

    /**
     * @function removeModulesWithoutElements
     * @param modules {array}
     * @param [submodName] {string} -  The key on the module object on which the submodules can be found.
     *                                    Defaults to 'submodules'
     * @param [elemName] {string} -  The key on the submodule object on which the elements can be found.
     *                                    Defaults to 'elements'
     * @param [hide] {Boolean} - If true, the (sub)module will have an attribute 'hide' set to true
     *                           rather than filtering from containing array
     * @return {array} - The filtered modules array
     * */
    this.removeModulesWithoutElements = (modules, submodName = 'submodules', elemName = 'elements', hide) => {

      // remove submodules without elements
      modules.forEach((module) => {
        module[submodName] = this.filterOrHideEmptyChildren(module[submodName], elemName, hide);
      });

      return this.filterOrHideEmptyChildren(modules, submodName, hide);
    };

    this.filterOrHideEmptyChildren = (parentsArr, childKey, hide) => {
      if (!hide) {
        return parentsArr.filter((parent) => angular.isDefined(parent[childKey]) && parent[childKey].length > 0);
      }

      parentsArr.forEach((parent) => {
        const childrenLength = parent[childKey].length;
        let hiddenChildrenCount = 0;

        for (let i = 0; i < childrenLength; i++) {
          if (parent[childKey][i].hide) {
            hiddenChildrenCount += 1;
          }
        }

        parent.hide = hiddenChildrenCount === childrenLength;
      });
      return parentsArr;
    };

    /**
     * @function getSubmoduleByElement
     * @param modules {array}
     * @param element {object}
     * @return {object} - The submodule which contains the element
     * */
    this.getSubmoduleByElement = function (modules, element) {
      const module = _.find(modules, function (mod) {
        return mod._id === element.moduleId;
      });
      const submodule = _.find(module.submodules, function (submod) {
        return submod._id === element.submoduleId;
      });
      return submodule;
    };

    /**
     * Filters inactive modules, submodules and elements
     * @function filterInactive
     * @param modules {array}
     * @param [options] {object} - Object which can contain upto 3 keys: modules, submodules, elements
     * @param [options.modules] {boolean} - whether to filter inactive modules
     * @param [options.submodules] {boolean} - whether to filter inactive submodules
     * @param [options.elements] {boolean} - whether to filter inactive elements
     * @return {array} - filtered modules
     * */
    this.filterInactive = function (modules, options) {
      var internalOptions = { key: 'active', value: true };
      var filterOptions = angular.isUndefined(options) ? internalOptions : Object.assign({}, options, internalOptions);

      return this.filterElementsFromModules(modules, filterOptions);
    };

    /**
     * Filters elements on specific project level from modules
     * @function filterElementsOnProjectLevel
     * @param modules {array}
     * @param projectLevel {number}
     * @param [overrideOptions] {object} - options fields to add/override that are passed to filterElementsFromModules
     * @return {array} - filtered modules
     * */
    this.filterElementsOnProjectLevel = function (modules, projectLevel, overrideOptions = {}) {
      const internalOptions = {
        key: 'projectLevel',
        value: projectLevel,
        modules: false,
        submodules: false,
        elements: true
      };

      return this.filterElementsFromModules(modules, Object.assign({}, internalOptions, overrideOptions));
    };

    /**
     * Filters elements on specific construction from modules
     * @function filterElementsOnConstructionId
     * @param modules {array}
     * @param constructionId {string}
     * @return {array} - filtered modules
     * */
    this.filterElementsOnConstructionId = function (modules, constructionId) {
      var internalOptions = {
        key: 'constructionId',
        value: constructionId,
        modules: false,
        submodules: false,
        elements: true,
        hide: true
      };

      return this.filterElementsFromModules(modules, internalOptions);
    };

    /**
     * Filters elements from modules array based on specific key and value
     * @function filterElementsFromModules
     * @param modules {array}
     * @param [options] {object} - Object which can contain upto 3 keys: modules, submodules, elements
     * @param [options.modules] {boolean} - whether to filter modules
     * @param [options.submodules] {boolean} - whether to filter submodules
     * @param [options.elements] {boolean} - whether to filter elements
     * @param options.key {String} - Element key to check value against
     * @param options.value {Any} - Value to check element[key] against
     * @param options.hide {Boolean} - Set hide is true rather than filtering the item
     * @return {array} - filtered modules
     * */
    this.filterElementsFromModules = function (modules, options) {
      options.modules = angular.isUndefined(options.modules) ? true : options.modules;
      options.submodules = angular.isUndefined(options.submodules) ? true : options.submodules;
      options.elements = angular.isUndefined(options.elements) ? true : options.elements;

      return modules.filter((module) => {
        module.submodules = module.submodules.filter((submodule) => {
          submodule.elements = submodule.elements.filter((element) => {
            return this.filterOrNot(element, 'elements', options);
          });
          return this.filterOrNot(submodule, 'submodules', options);
        });
        return this.filterOrNot(module, 'modules', options);
      });
    };

    this.filterOrNot = function (item, type, options) {
      if (!angular.isArray(options.value)) {
        if (!options.hide) {
          return options[type] ? item[options.key] === options.value : true;
        } else if (options.hide && options[type] && item[options.key] !== options.value) {
          item.hide = true;
        }
        return item;
      }

      if (!options.hide) {
        return options[type] ? options.value.includes(item[options.key]) : true;
      } else if (options.hide && options[type] && !options.value.includes(item[options.key])) {
        item.hide = true;
      }

      return item;
    };

    this.shallowClone = (modules, options) => {
      modules.forEach((mod) => {
        if (options.modules) {
          mod[options.cloneKey] = mod[options.modules];
        }

        mod.submodules.forEach((submod) => {
          if (options.submodules) {
            submod[options.cloneKey] = submod[options.submodules];
          }

          submod.elements.forEach((elem) => {
            if (options.elements) {
              elem[options.cloneKey] = elem[options.elements];
            }
          });
        });
      });
    };

    /**
     * Extract all elements from modules
     * @function getElementArray
     * @param modules {Array}
     * @return {Array} - array of elements
     *
     * */
    this.getElementArray = function (modules) {
      var elements = [];

      modules.forEach(function (module) {
        module.submodules.forEach(function (submodule) {
          submodule.elements.forEach(function (element) {
            elements.push(element);
          });
        });
      });

      return elements;
    };

    /**
     * Add module name to submodule, add module name and submodule name to element
     * @function enrichWithModuleAndSubmoduleInfo
     * @param modules {Array}
     * @return {Array}
     * */
    this.enrichWithModuleAndSubmoduleInfo = function (modules) {
      modules.forEach(function (module) {
        module.submodules.forEach(function (submodule) {
          submodule.moduleName = module.name;
          submodule.moduleId = module._id;
          submodule.elements.forEach(function (element) {
            element.moduleName = module.name;
            element.submoduleName = submodule.name;
            element.moduleId = module._id;
            element.submoduleId = submodule._id;
          });
        });
      });
    };

    this.filterElementsArray = function (elements, key, value) {
      return elements.filter(function (element) {
        return element[key] === value;
      });
    };

    /**
     * Find element from modules array
     * @function findElementByElementId
     * @param modules {Array}
     * @param elementId {String}
     * @return {Object} - elementObj
     * */
    this.findElementByElementId = function (modules, elementId) {
      var returnElement,
        moduleCount = modules.length;
      for (var mi = 0; mi < moduleCount; mi++) {
        var submodules = modules[mi].submodules,
          submoduleCount = submodules.length;
        for (var si = 0; si < submoduleCount; si++) {
          var elements = submodules[si].elements,
            elementCount = elements.length;
          for (var ei = 0; ei < elementCount; ei++) {
            var element = elements[ei];
            if (element._id === elementId) {
              mi = si = ei = Number.MAX_VALUE; // break;
              returnElement = angular.copy(element);
            }
          }
        }
      }
      return returnElement;
    };

    this.setDisabledElements = function (elements, knowledgeId) {
      elements.forEach(function (element) {
        element.disabled = knowledgeId === 'all' ? false : element.knowledgeLevel !== knowledgeId;
      });
    };

    this.setDisabledModulesAndSubmodules = function (modules, knowledgeId) {
      modules.forEach(function (module) {
        module.disabled = true;
        module.submodules.forEach(function (submodule) {
          submodule.disabled = true;
          let elementHasKnowledgeLevelIdx = submodule.elements.findIndex((e) => e.knowledgeLevel === knowledgeId);
          elementHasKnowledgeLevelIdx = knowledgeId === 'all' ? 0 : elementHasKnowledgeLevelIdx;

          if (elementHasKnowledgeLevelIdx > -1) {
            module.disabled = false;
            submodule.disabled = false;
          }
        });
      });
    };
  }
})();
